/**
 * Dismiss Dialog Component
 *
 * Confirmation dialog for dismissing an assessment from the queue.
 * Includes an optional reason text field.
 */

import { useState, useEffect } from "react";
import { XCircle, Ban } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { trackCareQueueEvent } from "../../lib/tracking";

interface DismissDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason?: string) => void;
  isPending?: boolean;
}

export function DismissDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
}: DismissDialogProps) {
  const [reason, setReason] = useState("");

  // Analytics: emit when dialog opens
  useEffect(() => {
    if (open) {
      trackCareQueueEvent("care_queue_dialog_opened", { dialogType: "dismiss" });
    }
  }, [open]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      trackCareQueueEvent("care_queue_dialog_action", {
        dialogType: "dismiss",
        action: "cancel",
      });
      setReason("");
    }
    onOpenChange(newOpen);
  };

  const handleConfirm = () => {
    trackCareQueueEvent("care_queue_dialog_action", {
      dialogType: "dismiss",
      action: "confirm",
    });
    onConfirm(reason.trim() || undefined);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="p-0 gap-0 overflow-hidden bg-card [&>button]:hidden"
        style={{ width: "560px", maxWidth: "calc(100vw - 2rem)" }}
      >
        {/* Custom Teal Header */}
        <div className="px-8 py-5 relative rounded-t bg-primary">
          <button
            onClick={() => handleOpenChange(false)}
            className="absolute top-5 right-5 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
            aria-label="Close"
          >
            <XCircle className="h-6 w-6" />
          </button>
          <h2
            className="text-primary-foreground"
            style={{ fontSize: "20px", fontWeight: 700 }}
          >
            Dismiss Assessment
          </h2>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-5 bg-card">
          {/* Confirmation message */}
          <div className="p-4 rounded bg-background">
            <p className="text-sm text-foreground">
              Are you sure you want to dismiss this assessment? It will be
              removed from the queue.
            </p>
          </div>

          {/* Optional Reason Input */}
          <div className="space-y-2">
            <label
              htmlFor="dismiss-reason"
              className="text-sm font-medium text-foreground"
            >
              Reason (optional)
            </label>
            <Input
              id="dismiss-reason"
              type="text"
              placeholder="Enter reason for dismissal..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{ border: "1px solid rgba(209, 210, 219, 1.00)" }}
              disabled={isPending}
            />
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/50">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            <Ban className="h-4 w-4 mr-2" />
            {isPending ? "Dismissing..." : "Dismiss"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
